<script setup lang="ts">
/**
 * One work item standing on the `/executions` board on its own (#1830): unclaimed in Backlog, or held or worked by a
 * run that is on no execution, in that run's lane. Once the run holding it is on an execution, the item rides that
 * execution's card instead (`ExecutionCard`), so an item is one card for its whole life. Drawn on the kit Card, as
 * the execution card is, so it states no surface of its own.
 */
import { ArrowRight } from '@lucide/vue'
import type { WorkItemCard } from '@zerohuman/shared'
import { Button, Card, CardContent, ZhTooltip } from '@zerohuman/ui'
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import { formatAgo } from '@/api'
import ExecutionStatus from '@/components/ExecutionStatus.vue'
import WorkItemActions from '@/components/WorkItemActions.vue'
import { PRIORITY_LABELS } from '@/lib/boardLanes'
import { statusLabel } from '@/lib/statusTone'

const props = defineProps<{ item: WorkItemCard; now: number }>()
/** An action changed the item (#1831): the board reads itself again. */
const emit = defineEmits<{ changed: [] }>()

const addedAgo = computed(() => formatAgo(Math.max(0, props.now - new Date(props.item.createdAt).getTime())))
const priority = computed(() => (props.item.priority ? PRIORITY_LABELS[props.item.priority] : null))
</script>

<template>
    <Card :data-work-item-id="item.id" :data-work-item-state="item.state" class="text-sm">
        <CardContent>
            <div class="flex items-start justify-between gap-2">
                <div class="min-w-0">
                    <RouterLink :to="`/work-items/${item.id}`" class="hover:text-primary block font-medium">{{ item.title }}</RouterLink>
                    <span class="text-muted-foreground mt-0.5 block text-xs">Work item · {{ item.kind }}<span v-if="priority"> · {{ priority }}</span></span>
                </div>
                <!-- A link, drawn as the kit icon Button: the RouterLink is the element, the Button its look. -->
                <Button as-child variant="ghost" size="icon-xs">
                    <RouterLink :to="`/work-items/${item.id}`" :aria-label="`Open ${item.title}`"><ArrowRight :size="14" /></RouterLink>
                </Button>
            </div>
            <div class="mt-2">
                <!-- The state pill says what the lane cannot: a proposed item waits for a person before any run can take it. -->
                <ExecutionStatus :status="item.state" />
                <p v-if="item.run" class="text-muted-foreground mt-1.5 text-xs" data-testid="work-item-run">
                    {{ item.closedAt ? 'Worked by' : 'Held by' }}
                    <RouterLink :to="`/runs/${item.run.id}`" class="underline underline-offset-2">run {{ item.run.id.slice(0, 8) }}</RouterLink>
                    · {{ statusLabel(item.run.status) }}
                </p>
                <!-- #575: a held item that still names a run is one whose pipeline stopped short. -->
                <p v-if="item.run && item.state === 'held'" class="text-muted-foreground mt-1 text-xs" data-testid="work-item-stopped">Its pipeline stopped. Retry or clear the run on Blockers to go on.</p>
            </div>
            <p class="text-muted-foreground mt-3 text-xs">{{ item.team?.name ?? 'No team' }}</p>
            <!-- #431: an item another team's run sent says where it came from: that team, and the run that sent it. -->
            <p v-if="item.origin" class="text-muted-foreground mt-1 text-xs" data-testid="work-item-origin">
                from {{ item.origin.teamName }} ·
                <RouterLink :to="`/runs/${item.origin.runId}`" class="underline underline-offset-2">run {{ item.origin.runId.slice(0, 8) }}</RouterLink>
            </p>
            <p class="text-muted-foreground mt-1 text-xs tabular-nums">
                <ZhTooltip :text="new Date(item.createdAt).toLocaleString()"><span class="whitespace-nowrap">Added {{ addedAgo }}</span></ZhTooltip>
            </p>
            <!-- A person shapes the list from the card (#1831): approve, decline, move, hold, release, drop. -->
            <WorkItemActions v-if="!item.closedAt" :item="{ id: item.id, title: item.title, state: item.state, claimed: item.run !== null && item.state === 'in_progress' }" compact class="mt-3" @changed="emit('changed')" />
        </CardContent>
    </Card>
</template>
